import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2 } from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import { validateForm } from '../utils/validation';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isOthers: false
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [formTouched, setFormTouched] = useState(false);

  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchCategories();
    }
  }, [dataFetched]);

  const fetchCategories = async (retryCount = 0) => {
    try {
      const response = await axios.get('/admin/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);

      // Handle 429 with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => {
          fetchCategories(retryCount + 1);
        }, 1000);
        return;
      }

      // Use fallback sample data
      const sampleCategories = [
        { id: 1, name: 'Electronics', description: 'Electronic items and gadgets' },
        { id: 2, name: 'Clothing', description: 'Apparel and accessories' },
        { id: 3, name: 'Books', description: 'Books and educational materials' }
      ];
      setCategories(sampleCategories);

      if (error.response?.status === 429) {
        toast('Server is busy, using sample data', { icon: '⚠️' });
      } else {
        toast('Using sample data - API connection failed', { icon: '⚠️' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCategory) {
        await axios.put(`/admin/categories/${editingCategory.id}`, formData);
        toast.success('Category updated successfully!');
      } else {
        await axios.post('/admin/categories', formData);
        toast.success('Category created successfully!');
      }

      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', isOthers: false });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      isOthers: category.isOthers || false
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await axios.delete(`/admin/categories/${categoryId}`);
        toast.success('Category deleted successfully!');
        fetchCategories();
      } catch (error) {
        toast.error('Failed to delete category');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', isOthers: false });
    setEditingCategory(null);
    setShowModal(false);
  };

  if (loading && categories.length === 0) {
    return <div className="max-w-7xl mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Category Management</h1>
            <p className="text-gray-600">Manage product categories</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{category.name}</h3>
                {category.isOthers && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                    Others Category
                  </span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="text-[#0A5F38] hover:text-blue-900"
                  title="Edit Category"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete Category"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              {category.description || 'No description available'}
            </p>
          </div>
        ))}
      </div>

      {categories.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No categories found.</p>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary mt-4"
          >
            Create Your First Category
          </button>
        </div>
      )}

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#F8F5E6] rounded-2xl max-w-md w-full shadow-2xl border border-[#C5A57D]/20">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#0A5F38]">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-[#C5A57D] hover:text-[#B8985E]"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    required
                    placeholder="Enter category name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    rows="3"
                    placeholder="Enter category description"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isOthers"
                    checked={formData.isOthers}
                    onChange={(e) => setFormData({ ...formData, isOthers: e.target.checked })}
                    className="h-4 w-4 text-[#0A5F38] focus:ring-[#0A5F38] border-[#C5A57D]/50 rounded"
                  />
                  <label htmlFor="isOthers" className="ml-2 block text-sm text-slate-900">
                    Is "Others" Category?
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Check this for a generic category that allows employees to enter custom product names manually.
                </p>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-[#C5A57D]/30 text-slate-700 rounded-lg hover:bg-[#F3EFE0] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#C5A57D] text-[#0A5F38] rounded-lg font-bold hover:bg-[#B8985E] transition-colors"
                  >
                    {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;



